import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

export default function ProfProjectsList() {
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showModal, setShowModal] = useState(false);
    
    // New project form
    const [title, setTitle] = useState('');
    const [clientName, setClientName] = useState('');
    const [creating, setCreating] = useState(false);
    
    const navigate = useNavigate();
    const API_BASE = process.env.REACT_APP_API_URL || '';

    const handleDeleteProject = async (e, projectId, projectTitle) => {
        e.preventDefault(); // Prevent Link navigation
        e.stopPropagation();
        if (!window.confirm(`האם אתה בטוח שברצונך למחוק את הפרויקט "${projectTitle}"? \nפעולה זו אינה ניתנת לביטול.`)) return;
        try {
            const token = localStorage.getItem('pro_token') || sessionStorage.getItem('pro_token') || localStorage.getItem('admin_token') || sessionStorage.getItem('admin_token');
            const res = await fetch(`${API_BASE}/api/prof-projects/${projectId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!res.ok) throw new Error('שגיאה במחיקת הפרויקט');
            setProjects(prev => prev.filter(p => p._id !== projectId));
        } catch (err) {
            console.error(err);
            alert(err.message);
        }
    };

    useEffect(() => {
        fetchProjects();
    }, []);

    const fetchProjects = async () => {
        try {
            const token = localStorage.getItem('pro_token') || sessionStorage.getItem('pro_token') || localStorage.getItem('admin_token') || sessionStorage.getItem('admin_token');
            const res = await fetch(`${API_BASE}/api/prof-projects`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!res.ok) throw new Error('שגיאה בטעינת הפרויקטים');
            const data = await res.json();
            setProjects(data);
        } catch (err) {
            console.error(err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateProject = async (e) => {
        e.preventDefault();
        setCreating(true);
        try {
            const token = localStorage.getItem('pro_token') || sessionStorage.getItem('pro_token') || localStorage.getItem('admin_token') || sessionStorage.getItem('admin_token');
            const res = await fetch(`${API_BASE}/api/prof-projects`, {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}` 
                },
                body: JSON.stringify({ title, clientName })
            });

            if (!res.ok) throw new Error('שגיאה ביצירת הפרויקט');
            
            const newProject = await res.json();
            setShowModal(false);
            navigate(`/professional/projects/${newProject._id}`);
        } catch (err) {
            console.error(err);
            alert(err.message);
        } finally {
            setCreating(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50 font-sans" dir="rtl">
            <div className="max-w-6xl mx-auto px-4 py-12">
                <div className="flex flex-col md:flex-row justify-between items-center mb-10 gap-4">
                    <div>
                        <Link to="/professionals" className="text-sm font-bold text-gray-500 hover:text-blue-600 mb-2 inline-block transition-colors">
                            &rarr; חזרה לכלים מקצועיים
                        </Link>
                        <h1 className="text-4xl font-black text-slate-900">הפרויקטים שלי</h1>
                        <p className="text-slate-500 mt-2">ניהול רשימות פריטים, סיכומי PDF ומפרטים</p>
                    </div>
                    <button 
                        onClick={() => setShowModal(true)}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-bold shadow-lg shadow-blue-200 transition-all flex items-center gap-2"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        פרויקט חדש
                    </button>
                </div>

                {error && (
                    <div className="bg-red-50 text-red-600 p-4 rounded-xl text-center font-bold mb-8">
                        {error}
                    </div>
                )}

                {projects.length === 0 && !error ? (
                    <div className="bg-white rounded-3xl p-12 text-center shadow-sm border border-slate-100">
                        <div className="w-20 h-20 bg-blue-50 text-blue-500 rounded-full flex items-center justify-center mx-auto mb-6">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                            </svg>
                        </div>
                        <h3 className="text-2xl font-bold text-slate-800 mb-2">אין לך פרויקטים עדיין</h3>
                        <p className="text-slate-500 mb-6 max-w-sm mx-auto">צור את הפרויקט הראשון שלך כדי להתחיל לנהל פריטים ולפרק מפרטי PDF אוטומטית.</p>
                        <button onClick={() => setShowModal(true)} className="text-blue-600 font-bold hover:underline">
                            + צור פרויקט חדש
                        </button>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {projects.map(proj => (
                            <Link key={proj._id} to={`/professional/projects/${proj._id}`} className="group relative bg-white block rounded-3xl p-6 shadow-sm hover:shadow-xl border border-slate-100 transition-all duration-300 hover:-translate-y-1">
                                {/* Delete Button */}
                                <button
                                    onClick={(e) => handleDeleteProject(e, proj._id, proj.title)}
                                    className="absolute top-4 left-4 w-8 h-8 bg-slate-50 hover:bg-red-50 text-slate-300 hover:text-red-500 rounded-full flex items-center justify-center transition-colors z-10 opacity-0 group-hover:opacity-100"
                                    title="מחק פרויקט"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                </button>
                                <h3 className="text-xl font-black text-slate-800 mb-1 group-hover:text-blue-600 transition-colors pr-2">
                                    {proj.title}
                                </h3>
                                <p className="text-sm text-slate-500 mb-4 pr-2 font-medium">לקוח: {proj.clientName || 'לא צוין'}</p>
                                
                                <div className="flex items-center gap-4 border-t border-slate-100 pt-4 mt-2 pr-2 text-sm text-slate-400">
                                    <div className="flex items-center gap-1.5">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                        </svg>
                                        {new Date(proj.createdAt).toLocaleDateString('he-IL')}
                                    </div>
                                    <div className="flex items-center gap-1.5">
                                        <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                                        {proj.items?.length || 0} פריטים
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                )}
            </div>

            {/* Create Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-3xl w-full max-w-md p-8 shadow-2xl scale-100 animate-in fade-in zoom-in duration-200">
                        <div className="flex justify-between items-center mb-8">
                            <h2 className="text-2xl font-black text-slate-800">פרויקט חדש</h2>
                            <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-600 bg-slate-100 w-8 h-8 rounded-full flex items-center justify-center">
                                &times;
                            </button>
                        </div>
                        <form onSubmit={handleCreateProject} className="space-y-5">
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-2">שם הפרויקט *</label>
                                <input 
                                    type="text" 
                                    required 
                                    value={title}
                                    onChange={e => setTitle(e.target.value)}
                                    className="w-full p-3 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                                    placeholder="לדוגמה: וילה משפחת כהן"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-2">שם לקוח</label>
                                <input 
                                    type="text" 
                                    value={clientName}
                                    onChange={e => setClientName(e.target.value)}
                                    className="w-full p-3 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                                    placeholder="אופציונלי"
                                />
                            </div>
                            <div className="pt-4 flex gap-3">
                                <button 
                                    type="submit" 
                                    disabled={creating}
                                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-4 rounded-xl transition-colors disabled:opacity-50"
                                >
                                    {creating ? 'יוצר פרויקט...' : 'צור פרויקט'}
                                </button>
                                <button 
                                    type="button" 
                                    onClick={() => setShowModal(false)}
                                    className="bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold py-3 px-6 rounded-xl transition-colors"
                                >
                                    ביטול
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
